import { ZohoAuth } from '@/types';
import { ZOHO_ENDPOINTS, ZOHO_SCOPES, STORAGE_KEYS } from '@/utils/constants';

class ZohoAuthService {
  private clientId: string;
  private clientSecret: string;
  private redirectUri: string;

  constructor() {
    this.clientId = import.meta.env.VITE_ZOHO_CLIENT_ID || '';
    this.clientSecret = import.meta.env.VITE_ZOHO_CLIENT_SECRET || '';
    this.redirectUri = import.meta.env.VITE_ZOHO_REDIRECT_URI || '';
  }

  // Générer l'URL d'autorisation
  getAuthUrl(): string {
    const params = new URLSearchParams({
      client_id: this.clientId,
      response_type: 'code',
      redirect_uri: this.redirectUri,
      scope: ZOHO_SCOPES.join(','),
      access_type: 'offline',
      prompt: 'consent'
    });

    const authUrl = `${ZOHO_ENDPOINTS.AUTH}?${params.toString()}`;
    
    return authUrl;
  }

  // Échanger le code d'autorisation contre un token
  async exchangeCodeForToken(code: string): Promise<ZohoAuth> {
    const params = new URLSearchParams({
      client_id: this.clientId,
      client_secret: this.clientSecret,
      code: code,
      redirect_uri: this.redirectUri,
      grant_type: 'authorization_code'
    });

    // Utiliser le backend proxy pour éviter les problèmes CORS
    const url = `${import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001'}/api/zoho/oauth/oauth/v2/token`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString()
    });

    if (!response.ok) {
      throw new Error(`Erreur d'authentification: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Créer l'objet d'authentification de base
    const auth: ZohoAuth = {
      accessToken: data.access_token || '',
      refreshToken: data.refresh_token || '',
      expiresAt: new Date(Date.now() + (data.expires_in || 3600) * 1000),
      userId: data.user_id || '',
      userEmail: data.user_email || ''
    };

    // Générer les informations utilisateur à partir du token
    if (auth.userEmail) {
      const emailParts = auth.userEmail.split('@');
      if (emailParts[0]) {
        const nameFromEmail = emailParts[0]
          .replace(/[._-]/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        auth.userName = nameFromEmail;
        const nameParts = nameFromEmail.split(' ');
        auth.userFirstName = nameParts[0] || 'Utilisateur';
        auth.userLastName = nameParts.slice(1).join(' ') || 'Zoho';
      }
    } else {
      // Aucun email disponible, utiliser des valeurs par défaut
      auth.userName = 'Utilisateur Zoho';
      auth.userFirstName = 'Utilisateur';
      auth.userLastName = 'Zoho';
    }
    
    return auth;
  }

  // Rafraîchir le token
  async refreshToken(refreshToken: string): Promise<ZohoAuth> {
    const params = new URLSearchParams({
      client_id: this.clientId,
      client_secret: this.clientSecret,
      refresh_token: refreshToken,
      grant_type: 'refresh_token'
    });

    // Utiliser le backend proxy pour éviter les problèmes CORS
    const url = `${import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001'}/api/zoho/oauth/oauth/v2/token`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString()
    });

    if (!response.ok) {
      throw new Error(`Erreur de rafraîchissement: ${response.statusText}`);
    }

    const data = await response.json();
    
    return {
      accessToken: data.access_token,
      refreshToken: data.refresh_token || refreshToken,
      expiresAt: new Date(Date.now() + data.expires_in * 1000),
      userId: data.user_id || '',
      userEmail: data.user_email || ''
    };
  }

  // Sauvegarder l'authentification
  saveAuth(auth: ZohoAuth): void {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, auth.accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, auth.refreshToken);
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify({
      userId: auth.userId,
      userEmail: auth.userEmail,
      userName: auth.userName,
      userFirstName: auth.userFirstName,
      userLastName: auth.userLastName,
      userPhoto: auth.userPhoto,
      expiresAt: auth.expiresAt.toISOString()
    }));
  }

  // Charger l'authentification
  loadAuth(): ZohoAuth | null {
    const accessToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);

    if (!accessToken || !refreshToken || !userData) {
      return null;
    }

    try {
      const user = JSON.parse(userData);
      const expiresAt = new Date(user.expiresAt);

      // Vérifier si le token a expiré
      if (expiresAt <= new Date()) {
        this.clearAuth();
        return null;
      }

      return {
        accessToken,
        refreshToken,
        expiresAt,
        userId: user.userId,
        userEmail: user.userEmail,
        userName: user.userName,
        userFirstName: user.userFirstName,
        userLastName: user.userLastName,
        userPhoto: user.userPhoto
      };
    } catch (error) {
      this.clearAuth();
      return null;
    }
  }

  // Effacer l'authentification
  clearAuth(): void {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  }

  // Vérifier si l'utilisateur est authentifié
  isAuthenticated(): boolean {
    const auth = this.loadAuth();
    return auth !== null && auth.expiresAt > new Date();
  }

  // Obtenir le token d'accès valide
  async getValidToken(): Promise<string | null> {
    const auth = this.loadAuth();
    
    if (!auth) {
      return null;
    }

    // Si le token expire dans moins de 5 minutes, le rafraîchir
    if (auth.expiresAt.getTime() - Date.now() < 5 * 60 * 1000) {
      try {
        const newAuth = await this.refreshToken(auth.refreshToken);
        this.saveAuth(newAuth);
        return newAuth.accessToken;
      } catch (error) {
        this.clearAuth();
        return null;
      }
    }

    return auth.accessToken;
  }

  // Récupérer les portails de l'utilisateur
  async getPortals(token: string): Promise<Array<{ id: string; name: string; portal_name: string }>> {
    try {
      // Utiliser le backend proxy pour éviter les problèmes CORS
      const url = `${import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001'}/api/zoho/projects/api/v3/portals`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Essayer différents formats possibles
        if (data.portals && Array.isArray(data.portals)) {
          return data.portals;
        } else if (data.data && Array.isArray(data.data)) {
          return data.data;
        } else if (Array.isArray(data)) {
          return data;
        } else {
          return [];
        }
      } else {
        return [];
      }
    } catch (error) {
      return [];
    }
  }
}

export const zohoAuthService = new ZohoAuthService(); 